import React, { useEffect, useMemo, useState } from 'react';
import styles from '../styles/stepProfile.module.css';
import { obtenerContenidoPlanificador, PlannerContentOption } from '../api/api';

interface Props {
  perfil: string;
  setPerfil: (p: string) => void;
  onNext: () => void | Promise<void>;
  onBack: () => void;
  loading?: boolean;
}

type Option = PlannerContentOption & {
  id: string;
  label: string;
  img: string;
  description: string;
};

const toOption = (option: PlannerContentOption): Option => ({
  ...option,
  description: option.description || option.label,
});

const StepProfile: React.FC<Props> = ({ perfil, setPerfil, onNext, onBack, loading = false }) => {
  const [cmsComoViajasOptions, setCmsComoViajasOptions] = useState<Option[]>([]);
  const [cmsApeteceOptions, setCmsApeteceOptions] = useState<Option[]>([]);
  const [selectedComoViajas, setSelectedComoViajas] = useState<string | null>(null);
  const [selectedApetece, setSelectedApetece] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    obtenerContenidoPlanificador()
      .then((content) => {
        if (!active) return;
        setCmsComoViajasOptions(content.comoViajas.map(toOption));
        setCmsApeteceOptions(content.apetece.map(toOption));
      })
      .catch((error) => {
        console.warn('[StepProfile] No se pudo cargar contenido de Directus', error);
      });

    return () => {
      active = false;
    };
  }, []);

  const comoViajasOptions = cmsComoViajasOptions;
  const apeteceOptions = cmsApeteceOptions;

  const selectedTop = useMemo(
    () => comoViajasOptions.find((o) => o.id === selectedComoViajas) || null,
    [comoViajasOptions, selectedComoViajas],
  );
  const selectedBottom = useMemo(
    () => apeteceOptions.find((o) => o.id === selectedApetece) || null,
    [apeteceOptions, selectedApetece],
  );

  // Cuando hay ambas selecciones, se construye el perfil automáticamente
  useEffect(() => {
    if (!selectedTop || !selectedBottom) return;
    setPerfil(`${selectedTop.description} y ${selectedBottom.description}`);
  }, [selectedTop, selectedBottom, setPerfil]);

  // ✅ Permitir continuar si hay texto manual en el input
  const canContinue = perfil.trim().length > 0 || (!!selectedTop && !!selectedBottom);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canContinue) return;
        onNext();
      }}
    >
      <h2 className={styles.title}>
        Cuéntanos sobre ti: ¿qué tipo de experiencias te gustaría vivir en tu viaje?
      </h2>

      {/* Input (manual o autogenerado) */}
      <div className={styles.profileGroup}>
        <div className={styles.field}>
          <input
            type="text"
            value={perfil}
            onChange={(e) => setPerfil(e.target.value)}
            placeholder="Ej: Quiero naturaleza y pueblos con encanto, soy español y tengo 33 años"
            required
          />
        </div>

        {!canContinue && (
          <p className={styles.helper}>
            Escribe tu perfil o elige <strong>cómo viajas</strong> y <strong>qué te apetece</strong>{' '}
            para continuar.
          </p>
        )}
      </div>

      {/* Selector visual */}
      <div className={styles.selectorWrap}>
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>¿Cómo viajas?</h3>
          <div className={styles.choicesRow}>
            {comoViajasOptions.map((opt) => {
              const selected = opt.id === selectedComoViajas;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.choice} ${selected ? styles.choiceSelected : ''}`}
                  onClick={() => setSelectedComoViajas(opt.id)}
                >
                  <span className={styles.choiceImgWrap}>
                    <img className={styles.choiceImg} src={opt.img} alt={opt.label} />
                  </span>
                  <span className={styles.choiceLabel}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.block}>
          <h3 className={styles.blockTitle}>¿Qué te apetece?</h3>
          <div className={styles.choicesRow}>
            {apeteceOptions.map((opt) => {
              const selected = opt.id === selectedApetece;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.choice} ${selected ? styles.choiceSelected : ''}`}
                  onClick={() => setSelectedApetece(opt.id)}
                >
                  <span className={styles.choiceImgWrap}>
                    <img className={styles.choiceImg} src={opt.img} alt={opt.label} />
                  </span>
                  <span className={styles.choiceLabel}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.buttons}>
        <button type="button" onClick={onBack} className={styles.backButton}>
          Atrás
        </button>
        <button type="submit" className={styles.nextButton} disabled={!canContinue || loading}>
          {loading ? 'Cargando...' : 'Continuar'}
        </button>
      </div>

    </form>
  );
};

export default StepProfile;
